import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Heart, MessageCircle, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { setPageMeta } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

const QUICK_CITIES = [
  "Milano", "Roma", "Napoli", "Torino", "Bologna", "Firenze",
  "Genova", "Bari", "Palermo", "Verona", "Padova", "Venezia",
];

export default function ReviewLanding() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [schoolsTotal, setSchoolsTotal] = useState("21.000");

  useEffect(() => {
    setPageMeta({
      title: "Per aiutare un genitore non basta un Open day — Racconta la tua esperienza sulla scuola dell'infanzia | Per manina",
      description:
        "Ma a te bastano due minuti: racconta la tua esperienza sulla scuola dell'infanzia di tuo figlio e aiuta chi sta ancora scegliendo quella giusta.",
      canonicalPath: "/recensisci",
      ogType: "website",
    });
    trackEvent("landing_view", { landing: "racconta-esperienza" });
    api
      .get("/schools", { params: { limit: 1 } })
      .then(({ data }) => {
        if (data?.total) setSchoolsTotal(data.total.toLocaleString("it-IT"));
      })
      .catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault();
    const q = query.trim();
    trackEvent("landing_search_submit", { landing: "racconta-esperienza", query_len: q.length });
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    navigate(`/cerca?${params.toString()}`);
  };

  return (
    <div className="bg-[#FAF9F6]" data-testid="review-landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/ads/ads-2-mother-observing.png"
            alt="Una mamma osserva i bambini entrare a scuola"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A362D]/70 via-[#1A362D]/55 to-[#1A362D]/85" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-40 sm:py-28 lg:py-32">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-[#F2C94C] tracking-wider" data-testid="landing-eyebrow">
              La community indipendente dei genitori italiani
            </p>
            <h1
              className="mt-4 font-display font-semibold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight fade-up"
              data-testid="landing-hero-title"
            >
              Per aiutare un genitore,<br />non basta un Open day.
              <br />
              <span className="text-[#F2C94C]">Ma a te bastano due minuti.</span>
            </h1>
            <p
              className="mt-6 font-ui text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl fade-up delay-1"
              data-testid="landing-hero-sub"
            >
              Una sola visita non racconta un anno (o più) di rapporti con le educatrici, di
              laboratori e sostegno, di pranzi alla mensa o elasticità negli orari... Solo chi{" "}
              <strong>quella scuola l'ha vissuta davvero</strong>{" "}
              <strong>può raccontarla</strong> al prossimo genitore che{" "}
              <strong>sta ancora scegliendo "quella giusta"!</strong>
            </p>
            <p
              className="mt-3 font-ui text-base sm:text-lg text-[#F2C94C] leading-relaxed max-w-2xl fade-up delay-1"
              data-testid="landing-hero-sub-2"
            >
              <strong>Trova la tua scuola e racconta la tua esperienza.</strong>
            </p>

            {/* SEARCH FORM */}
            <form
              onSubmit={submitSearch}
              className="mt-10 bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)] p-3 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 fade-up delay-2"
              data-testid="landing-search-form"
            >
              <div className="flex-1 flex items-center bg-[#FAF9F6] border border-[#E8E6E1] rounded-xl px-4 py-3">
                <Search size={20} className="text-[#8A9691] mr-2 shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nome della scuola, città o CAP…"
                  data-testid="landing-search-input"
                  className="flex-1 bg-transparent border-0 outline-none font-ui text-base text-[#1A362D] placeholder:text-[#8A9691]"
                  autoComplete="off"
                />
              </div>
              <button
                type="submit"
                data-testid="landing-search-submit"
                className="btn-primary text-base px-6 py-3 whitespace-nowrap"
              >
                Trova la tua scuola
              </button>
            </form>

            <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="landing-quick-cities">
              <span className="font-ui text-xs uppercase tracking-wider text-white/70 mr-1">
                Città:
              </span>
              {QUICK_CITIES.map((c) => (
                <button
                  key={c}
                  onClick={() => {
                    trackEvent("landing_city_click", { city: c });
                    navigate(`/migliori/${c.toLowerCase()}`);
                  }}
                  data-testid={`landing-city-${c.toLowerCase()}`}
                  className="px-3 py-1.5 rounded-full border border-white/40 text-white font-ui text-xs hover:bg-white hover:text-[#1A362D] transition-colors"
                >
                  {c}
                </button>
              ))}
              <Link
                to="/migliori"
                data-testid="landing-all-cities"
                className="px-3 py-1.5 rounded-full bg-[#C05C45] text-white font-ui text-xs hover:bg-[#A64A35] transition-colors"
              >
                Tutte le città →
              </Link>
            </div>

            <p
              className="mt-6 font-ui text-sm text-white/80"
              data-testid="landing-schools-count"
            >
              Oltre <strong>{schoolsTotal}</strong> scuole dell'infanzia italiane già mappate,
              in attesa della voce dei genitori.
            </p>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-3xl">
          <p className="label-eyebrow text-[#C05C45]">Perché la tua voce conta</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-[#1A362D] tracking-tight leading-tight">
            Il pomeriggio dell'Open Day è una vetrina.<br />
            La quotidianità è un'altra cosa.
          </h2>
          <p className="mt-6 font-ui text-base sm:text-lg text-[#3C4A44] leading-relaxed">
            Quando abbiamo scelto la scuola dell'infanzia per nostro figlio, abbiamo fatto quello che fanno tutti:
            due visite, compilato un modulo, tante domande rimaste in gola. Poi, a settembre, ci siamo
            accorti che <strong>la scuola vera si vede dopo</strong>: nelle merende dimenticate, nei
            disegni della domenica, nell'intesa con l'educatrice quando tuo figlio non vuole entrare.
          </p>
          <p className="mt-4 font-ui text-base sm:text-lg text-[#3C4A44] leading-relaxed">
            Se anche tu hai vissuto tutto questo,{" "}
            <strong>la tua esperienza è la mano tesa che vorresti aver trovato tu.</strong>
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6" data-testid="landing-benefit-1">
            <Heart className="text-[#C05C45]" size={26} />
            <h3 className="mt-4 font-display text-xl text-[#1A362D]">Un gesto per gli altri</h3>
            <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
              Bastano cinque minuti: racconta come si è trovato tuo figlio, cosa ti è piaciuto e cosa no.
              Il prossimo genitore ti ringrazierà.
            </p>
          </div>
          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6" data-testid="landing-benefit-2">
            <MessageCircle className="text-[#C05C45]" size={26} />
            <h3 className="mt-4 font-display text-xl text-[#1A362D]">Onesto, non perfetto</h3>
            <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
              Nessun voto d'esame. Racconta con parole tue: la mensa, gli spazi, le maestre, l'accoglienza.
              Ogni dettaglio aiuta.
            </p>
          </div>
          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6" data-testid="landing-benefit-3">
            <ShieldCheck className="text-[#C05C45]" size={26} />
            <h3 className="mt-4 font-display text-xl text-[#1A362D]">Con la tua identità, in sicurezza</h3>
            <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
              Login autentica solo con Google. Niente password. Le esperienze sono dei genitori,
              non dei brand.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F2EFEB] border-y border-[#E8E6E1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <p className="label-eyebrow text-[#C05C45]">Come funziona</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-[#1A362D] tracking-tight">
            Tre passi, cinque minuti.
          </h2>

          <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <li className="bg-white border border-[#E8E6E1] rounded-2xl p-6 relative" data-testid="landing-step-1">
              <div className="absolute -top-4 left-6 w-9 h-9 rounded-full bg-[#1A362D] text-white font-display flex items-center justify-center">
                1
              </div>
              <h3 className="mt-4 font-display text-lg text-[#1A362D]">Trova la tua scuola</h3>
              <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
                Cerca per nome, città o CAP. Se non la trovi, in un clic puoi aggiungerla.
              </p>
            </li>
            <li className="bg-white border border-[#E8E6E1] rounded-2xl p-6 relative" data-testid="landing-step-2">
              <div className="absolute -top-4 left-6 w-9 h-9 rounded-full bg-[#1A362D] text-white font-display flex items-center justify-center">
                2
              </div>
              <h3 className="mt-4 font-display text-lg text-[#1A362D]">Accedi con Google</h3>
              <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
                Un clic. Nessuna password. Verifichiamo solo che dietro l'esperienza ci sia una persona vera.
              </p>
            </li>
            <li className="bg-white border border-[#E8E6E1] rounded-2xl p-6 relative" data-testid="landing-step-3">
              <div className="absolute -top-4 left-6 w-9 h-9 rounded-full bg-[#1A362D] text-white font-display flex items-center justify-center">
                3
              </div>
              <h3 className="mt-4 font-display text-lg text-[#1A362D]">Racconta la tua esperienza</h3>
              <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed">
                Un voto, qualche parola, i dettagli che avresti voluto sapere tu. Sarà utile per anni.
              </p>
            </li>
          </ol>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
        <Sparkles className="text-[#C05C45] mx-auto" size={28} />
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-[#1A362D] tracking-tight leading-tight">
          La scelta della scuola<br />si fa con il cuore degli altri genitori.
        </h2>
        <p className="mt-6 font-ui text-base sm:text-lg text-[#3C4A44] leading-relaxed">
          Comincia da qui: cerca la scuola dell'infanzia di tuo figlio e lascia il tuo racconto.
        </p>
        <form
          onSubmit={submitSearch}
          className="mt-10 max-w-2xl mx-auto flex flex-col sm:flex-row items-stretch gap-2"
          data-testid="landing-final-search-form"
        >
          <div className="flex-1 flex items-center bg-white border border-[#E8E6E1] rounded-xl px-4 py-3">
            <MapPin size={20} className="text-[#8A9691] mr-2 shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nome della scuola, città o CAP…"
              data-testid="landing-final-search-input"
              className="flex-1 bg-transparent border-0 outline-none font-ui text-base text-[#1A362D] placeholder:text-[#8A9691]"
              autoComplete="off"
            />
          </div>
          <button
            type="submit"
            data-testid="landing-final-search-submit"
            className="btn-primary text-base px-6 py-3 whitespace-nowrap inline-flex items-center justify-center gap-2"
          >
            Racconta la tua esperienza ora <ArrowRight size={16} />
          </button>
        </form>

        <p className="mt-6 font-ui text-xs text-[#8A9691]">
          Non trovi la tua scuola?{" "}
          <Link
            to="/suggerisci"
            data-testid="landing-suggest-link"
            className="underline hover:text-[#C05C45]"
          >
            Segnalacela in un minuto
          </Link>
          .
        </p>
      </section>
    </div>
  );
}
